import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { ThemeService } from './services/theme.service';
import { IrisAppService } from './services/iris-app.service';
import { Web3Service } from './services/web3Service';
import { LOCAL_KEYS, normalizeLeaderboardReferralCode } from './shared/constant';
import { AccountService } from './services/account.service';
import { SolanaDeeplinkService } from './services/solana-deeplink.service';
import OneSignal from '@onesignal/capacitor-plugin';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  _defaultLang:string = 'en';

  constructor(
    private translate: TranslateService,
    private router: Router,
    private theme: ThemeService,
    private irisAppService: IrisAppService,
    private web3: Web3Service,
    private accountService: AccountService,
    private solanaDeeplink: SolanaDeeplinkService,
  ) {
    const browserCulture = this.translate.getBrowserCultureLang() || this.translate.getBrowserLang() || navigator.language || 'en';
    this._defaultLang = browserCulture.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    this.translate.setDefaultLang(this._defaultLang);
    this.translate.use(this._defaultLang);

    // Filet de sécurité : un chunk JS lazy-loadé (chaque page/onglet) peut 404 si index.html a été
    // servi depuis un cache (navigateur ou WebView Telegram, agressif) pointant vers des fichiers
    // hashés supprimés par un déploiement plus récent (outputHashing: all + mirror --delete côté
    // serveur, voir .htaccess). Ça laisse la coquille statique (barre d'onglets) affichée sans
    // aucun contenu, indéfiniment, jusqu'à un rechargement manuel — ici on le fait automatiquement,
    // une seule fois par session (sessionStorage) pour ne jamais boucler si l'erreur n'est pas liée
    // au cache (dans ce cas, l'erreur réapparaîtra après le reload et restera visible en console).
    this.router.events.subscribe(event => {
      // Une navigation réussie efface le garde-fou : si un NOUVEAU déploiement rend l'onglet
      // (resté ouvert des heures) à nouveau périmé plus tard dans la même session, l'auto-reload
      // doit pouvoir se redéclencher — le garde-fou sert seulement à éviter une boucle infinie de
      // reloads immédiatement consécutifs si l'erreur n'est en fait pas liée au cache.
      if (event instanceof NavigationEnd) { sessionStorage.removeItem('chunkReloadAttempted'); return; }
      if (!(event instanceof NavigationError)) return;
      const msg = String(event.error?.message ?? event.error ?? '');
      const isChunkLoadError = /Failed to fetch dynamically imported module|Loading chunk|ChunkLoadError/i.test(msg);
      if (!isChunkLoadError) return;
      if (sessionStorage.getItem('chunkReloadAttempted')) return;
      sessionStorage.setItem('chunkReloadAttempted', '1');
      window.location.reload();
    });
  }

  async ngOnInit() {
    // Filet de sécurité supplémentaire : toute cette méthode doit se terminer par une navigation
    // (vers la deep link, /tabs ou /welcome) pour que le contenu s'affiche — sans try/catch, une
    // exception non prévue sur l'un des appels ci-dessous (Preferences, forceWcClear...) arrête la
    // chaîne avant d'atteindre navigateByUrl et laisse l'écran vide (seule la coquille statique de
    // app.component.html reste visible), sans aucun message d'erreur visible pour l'utilisateur.
    try {
      await this.bootstrapAndNavigate();
    } catch (e) {
      console.error('[AppComponent] ngOnInit — erreur avant navigation, repli sur /tabs', e);
      this.router.navigateByUrl('/tabs');
    }
  }

  private async bootstrapAndNavigate() {
    const tg = (window as any).Telegram?.WebApp;
    const startParam: string = tg?.initDataUnsafe?.start_param ?? '';
    // index.html charge telegram-web-app.js sans condition : ce script crée toujours
    // window.Telegram.WebApp, même hors du client Telegram (avec initData vide dans ce cas) —
    // donc `tg` seul ne suffit pas à savoir si on est réellement dans une Mini App Telegram.
    const isTelegramMiniApp = !!tg?.initData;
    // Lien partagé ouvert directement dans le navigateur (ex: /token-swap?tokenAddress=...) :
    // on lit l'URL brute plutôt que this.router.url, dont l'état à ce stade de ngOnInit n'est
    // pas garanti (navigation initiale du Router pas nécessairement déjà résolue). Sans ce
    // garde, le navigateByUrl('/tabs' | '/welcome') plus bas écrasait systématiquement ce lien
    // et renvoyait l'utilisateur sur l'accueil, même quand l'app démarrait sur une deep link.
    const hasDeepLink = !isTelegramMiniApp && window.location.pathname !== '/' && window.location.pathname !== '';

    await this.capturePendingLeaderboardReferral(startParam);

    // BUG CORRIGÉ (2026-09-08, app Android native) : on faisait ici `forceWcClear()` à CHAQUE
    // démarrage — ça supprimait toute la session WalletConnect du localStorage (`wc@…`) tout en
    // gardant l'adresse persistée (`UserConnection`). Sur le web/desktop c'est sans effet (wallet
    // injecté), mais sur l'app Android native / la Mini App Telegram, WalletConnect est le SEUL
    // canal : résultat, l'app affichait « connecté » mais la moindre action on-chain (swap,
    // approve…) échouait en « Session expirée — reconnectez votre wallet », et il fallait
    // re-scanner le QR / ré-approuver dans le wallet À CHAQUE lancement (le WebView Android
    // redémarre souvent). On garde désormais la session (comportement standard de tout dapp WC) ;
    // on se contente de RÉCHAUFFER le relay en arrière-plan pour que la 1re signature ne parte pas
    // sur une socket froide. Le filet « relay mort après redémarrage » reste assuré par le bandeau
    // `needsReconnect` (plateformes concernées) + la reconnexion auto du provider WC à la 1re
    // requête, sans jamais détruire la session.
    this.web3.warmWcSession();

    // BUG CORRIGÉ (2026-09-11, signalé par l'utilisateur) — voir Web3Service.
    // watchInjectedAccountsChanged : sans ça, changer de compte directement dans l'extension
    // (MetaMask/Trust) sans repasser par "Connecter" dans l'app laissait toutes les pages
    // continuer à lire/écrire pour l'ANCIEN wallet indéfiniment (mauvais prix affiché sur
    // agent-subscribe entre autres). Ici plutôt que dans Web3Service pour éviter une dépendance
    // circulaire (AccountService dépend déjà de Web3Service). null = wallet déconnecté/verrouillé
    // côté extension — on laisse alors le cache tel quel plutôt que de déloguer l'utilisateur au
    // moindre verrouillage temporaire de MetaMask (repli prudent, jamais rencontré autrement).
    this.web3.watchInjectedAccountsChanged(address => {
      if (address) this.accountService.setAddress(address);
    });

    await this.loadLanguage();
    await this.theme.loadSaved();

    // Écran d'avertissement / conditions d'utilisation — bloquant, affiché une seule fois au tout
    // premier lancement (avant toute autre navigation, et avant de demander la permission de
    // notifications). `skipLocationChange` garde l'URL réelle intacte : après acceptation, la page
    // fait `window.location.reload()` et le bootstrap reprend le parcours d'origine (deep link,
    // startapp Telegram, /welcome ou /tabs) avec le flag posé. Voir disclaimer.page.ts.
    const disclaimer = await Preferences.get({ key: LOCAL_KEYS.DISCLAIMER_ACCEPTED });
    if (!disclaimer.value) {
      this.router.navigate(['/disclaimer'], { skipLocationChange: true });
      return;
    }

    this.initPushNotifications();
    this.initDeepLinks();

    // Telegram Mini App — startapp deep links
    if (tg) {
      tg.ready();
      // lc{courseId} or lc{courseId}-r-{wallet40hex}
      if (startParam.startsWith('lc')) {
        const parts = startParam.slice(2).split('-r-');
        const courseId = parts[0];
        if (courseId) {
          if (parts[1]) {
            await Preferences.set({ key: 'affiliateRef', value: '0x' + parts[1] });
          }
          this.router.navigateByUrl(`/learn-course?id=${courseId}`);
          return;
        }
      }
      // Legacy format
      if (startParam.startsWith('course_')) {
        const courseId = startParam.replace('course_', '');
        this.router.navigateByUrl(`/learn-course?id=${courseId}`);
        return;
      }
    }

    let response = await Preferences.get({ key: 'firstTime' });
    if (!response.value) {
      await Preferences.set({ key: 'firstTime', value: 'true' });
    }
    if (!hasDeepLink) {
      if (response.value) {
        this.router.navigateByUrl('/tabs');
      } else {
        const askRef = await this.shouldAskManualReferral();
        this.router.navigateByUrl(askRef ? '/welcome?askRef=1' : '/welcome');
      }
    }
  }

  // Filet de sécurité Android uniquement (2026-08-24) : un App Link (https://app.iristoken.io/...)
  // peut échouer à ouvrir l'app selon l'appareil/la version d'Android (vérification du domaine via
  // assetlinks.json non garantie partout, certains OEM la désactivant par défaut) — l'utilisateur
  // atterrit alors dans le navigateur ou l'app s'ouvre sans que `appUrlOpen` ait rien capturé.
  // Plutôt que de perdre l'attribution du parrain silencieusement, on propose la saisie manuelle
  // du code sur /welcome au tout premier lancement, seulement si aucun `ref` n'est déjà connu
  // (deep link déjà traité, ou aucun parrain). La Mini App Telegram (startapp=lc{id}-r-{wallet},
  // voir plus haut) n'est jamais concernée : elle ne passe jamais par /welcome avec ce flag, son
  // mécanisme reste inchangé.
  private async shouldAskManualReferral(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'android') return false;
    const existing = await Preferences.get({ key: LOCAL_KEYS.PENDING_REFERRAL_CODE });
    return !existing.value;
  }

  private async capturePendingLeaderboardReferral(startParam: string): Promise<void> {
    const code = normalizeLeaderboardReferralCode(startParam) || normalizeLeaderboardReferralCode(window.location.href);
    if (!code) return;
    await Preferences.set({ key: LOCAL_KEYS.PENDING_REFERRAL_CODE, value: code });
  }

  private initDeepLinks() {
    // Android App Links : https://app.iristoken.io/learn-course?id=2[&ref=0x...]
    // ou scheme custom   : irisapp://learn-course?id=2[&ref=0x...]
    //
    // `ref` (adresse du wallet affilié) est sauvegardé en Preferences AVANT la navigation —
    // même ordre que la branche Telegram startapp ci-dessus (lc{courseId}-r-{wallet40hex}),
    // pour que learn-course.page.ts le retrouve dès son tout premier ionViewWillEnter. Avant ce
    // fix (2026-08-24), `ref` était silencieusement jeté ici : le commentaire "ou Preferences
    // (Android deep link)" dans learn-course.page.ts ne correspondait à AUCUN code réel côté
    // App Links Android — seul le chemin Telegram startapp fonctionnait, contrairement à ce que
    // ce commentaire affirmait. Callback rendu async pour pouvoir attendre Preferences.set
    // avant de naviguer (Capacitor accepte un callback async sur addListener).
    App.addListener('appUrlOpen', async ({ url }) => {
      // Retour d'un deeplink wallet Solana (Phantom/Solflare) — irisapp://solana-callback?...
      // ou https://app.iristoken.io/solana-callback?... Traité ici (le contexte WebView survit),
      // puis retour sur la page qui avait lancé la connexion/signature.
      if (/(^|\/\/)solana-callback|\/solana-callback/.test(url)) {
        try {
          const parsed = new URL(url);
          const params: Record<string, string> = {};
          parsed.searchParams.forEach((v, k) => (params[k] = v));
          const returnUrl = (await this.solanaDeeplink.pendingReturnUrl()) || '/tabs';
          await this.solanaDeeplink.resolveCallback(params);
          this.router.navigateByUrl(returnUrl);
        } catch (e) {
          console.error('[AppComponent] solana-callback', e);
        }
        return;
      }

      const parsed = this.extractCourseLinkFromUrl(url);
      if (!parsed) {
        const code = normalizeLeaderboardReferralCode(url);
        if (code) await Preferences.set({ key: LOCAL_KEYS.PENDING_REFERRAL_CODE, value: code });
        return;
      }

      if (parsed.ref) {
        await Preferences.set({ key: 'affiliateRef', value: parsed.ref });
      }
      this.router.navigateByUrl(`/learn-course?id=${parsed.courseId}`);
    });
  }

  // `ref` retourné seulement s'il ressemble à une adresse EVM — même validation (minimale,
  // startsWith('0x')) que learn-course.page.ts:67, pour rester cohérent avec la vérification déjà
  // faite côté web plutôt que d'introduire une règle différente ici.
  private extractCourseLinkFromUrl(url: string): { courseId: string; ref: string | null } | null {
    try {
      const parsed = new URL(url);
      const isAppLink = parsed.pathname.includes('learn-course');
      const isCustomScheme = parsed.protocol === 'irisapp:' && parsed.host === 'learn-course';
      if (!isAppLink && !isCustomScheme) return null;

      const courseId = parsed.searchParams.get('id');
      if (!courseId) return null;

      const refParam = parsed.searchParams.get('ref');
      return { courseId, ref: refParam?.startsWith('0x') ? refParam : null };
    } catch {}
    return null;
  }

  private async initPushNotifications() {
    if (!Capacitor.isNativePlatform()) return;

    OneSignal.initialize('6225992d-d551-40c3-803e-8414ef77138b');
    await OneSignal.Notifications.requestPermission(true);

    OneSignal.User.pushSubscription.addEventListener('change', async (change) => {
      const playerId = change.current.id;
      if (!playerId) return;

      const player = await this.accountService.getAccount();
      const id = player?.Id;
      if (!id) return;

      const channel = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
      const invitedByCode = await this.getPendingReferralCode();
      this.irisAppService.registerDevice(id, channel, playerId, invitedByCode)
        .subscribe({ error: (e) => console.error('[OneSignal] registerDevice error', e) });
    });
  }

  private async getPendingReferralCode(): Promise<string> {
    const saved = await Preferences.get({ key: LOCAL_KEYS.PENDING_REFERRAL_CODE });
    return normalizeLeaderboardReferralCode(saved.value);
  }

  private async loadLanguage() {
    const { value } = await Preferences.get({ key: LOCAL_KEYS.LANGUAGE });
    const savedLang = this.normalizeLanguage(value);
    const browserCulture = this.translate.getBrowserCultureLang() || this.translate.getBrowserLang() || navigator.language || 'en';
    const browserLang = browserCulture.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    const lang = savedLang || browserLang;

    this._defaultLang = lang;
    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
  }

  private normalizeLanguage(value: string | null): 'fr' | 'en' | null {
    if (!value) {
      return null;
    }

    return value.toLowerCase().startsWith('fr') ? 'fr' : value.toLowerCase().startsWith('en') ? 'en' : null;
  }
}
